import { endOfDay, startOfDay } from "date-fns";
import { z } from "zod";

import { GoalsFilters } from "./components/Filters";

import { BulkSelectionQuery } from "../../../../../api/hooks";

export const getGoalsListSearchServiceFilters = (filters: Partial<GoalsFilters>, searchValue?: string) => {
	const finalFilters = {};

	if (filters.assignee && filters.assignee.length > 0) {
		finalFilters["assignee_in"] = filters.assignee.map((assignee) => assignee.id);
	}

	if (filters.createdDateAfter) {
		finalFilters["created_gt"] = endOfDay(filters.createdDateAfter).toISOString();
	}

	if (filters.createdDateBefore) {
		finalFilters["created_lt"] = startOfDay(filters.createdDateBefore).toISOString();
	}

	if (filters.dueDateAfter) {
		finalFilters["end_date_gt"] = endOfDay(filters.dueDateAfter).toISOString();
	}

	if (filters.dueDateBefore) {
		finalFilters["end_date_lt"] = startOfDay(filters.dueDateBefore).toISOString();
	}

	if (filters.category && filters.category.length > 0) {
		finalFilters["categories_in"] = filters.category.map((category) => category.id);
	}

	if (filters.status && filters.status.length > 0) {
		finalFilters["status_in"] = filters.status;
	}

	if (filters.transparency && filters.transparency.length > 0) {
		finalFilters["transparency_in"] = filters.transparency;
	}

	if (filters.outlook && filters.outlook.length > 0) {
		// if outlook contains value "none" then wen need to set the include not selected outlooks
		if (filters.outlook.some((outlook) => outlook.id === "none")) {
			const outlooks = filters.outlook.filter((outlook) => outlook.id !== "none");
			if (outlooks.length > 0) {
				finalFilters["color_in"] = outlooks.map((outlook) => outlook.id);
			}
			finalFilters["color_include_not_selected_eq"] = true;
		} else {
			finalFilters["color_in"] = filters.outlook.map((outlook) => outlook.id);
		}
	}

	if (filters.showSharedOnly && filters.showSharedOnly.length > 0) {
		finalFilters["is_shared_eq"] = filters.showSharedOnly[0] === "True";
	}

	if (filters.alignedTo) {
		finalFilters["ancestors_in"] = [filters.alignedTo?.id];
	}

	if (filters?.groups && filters.groups.length > 0) {
		finalFilters["group_in"] = filters.groups.map((group) => group.id);
	}

	if (searchValue) {
		finalFilters["name_re"] = searchValue;
	}

	return finalFilters;
};

export const convertFiltersForBulkSelectionQuery = (
	filters: Partial<GoalsFilters>,
	searchValue?: string
): BulkSelectionQuery[] => {
	const finalFilters: BulkSelectionQuery[] = [];

	if (filters?.status && filters.status.length > 0) {
		finalFilters.push({
			field: "status",
			operator: "in",
			value: filters.status,
		});
	}

	if (filters?.assignee && filters.assignee.length > 0) {
		finalFilters.push({
			field: "assignee",
			operator: "in",
			value: filters.assignee.map((assignee) => assignee.id),
		});
	}

	if (filters?.createdDateAfter) {
		finalFilters.push({
			field: "created",
			operator: "gt",
			value: endOfDay(filters.createdDateAfter).toISOString(),
		});
	}

	if (filters?.createdDateBefore) {
		finalFilters.push({
			field: "created",
			operator: "lt",
			value: startOfDay(filters.createdDateBefore).toISOString(),
		});
	}

	if (filters?.dueDateAfter) {
		finalFilters.push({
			field: "end_date",
			operator: "gt",
			value: endOfDay(filters.dueDateAfter).toISOString(),
		});
	}

	if (filters?.dueDateBefore) {
		finalFilters.push({
			field: "end_date",
			operator: "lt",
			value: startOfDay(filters.dueDateBefore).toISOString(),
		});
	}

	if (filters?.category && filters.category.length > 0) {
		finalFilters.push({
			field: "categories",
			operator: "in",
			value: filters.category.map((category) => category.id),
		});
	}

	if (filters?.transparency && filters.transparency.length > 0) {
		finalFilters.push({
			field: "transparency",
			operator: "in",
			value: filters.transparency,
		});
	}

	if (filters?.outlook && filters.outlook.length > 0) {
		finalFilters.push({
			field: "color",
			operator: "in",
			value: filters.outlook.map((outlook) => outlook.id),
		});
	}

	if (filters?.showSharedOnly && filters.showSharedOnly.length > 0) {
		finalFilters.push({
			field: "is_shared",
			operator: "eq",
			value: filters.showSharedOnly[0] === "True",
		});
	}

	if (filters?.alignedTo) {
		finalFilters.push({
			field: "ancestors",
			operator: "in",
			value: [filters.alignedTo?.id],
		});
	}

	if (filters?.groups && filters.groups.length > 0) {
		finalFilters.push({
			field: "group",
			operator: "in",
			value: filters.groups.map((group) => group.id),
		});
	}

	if (searchValue) {
		finalFilters.push({
			field: "name",
			operator: "re",
			value: searchValue,
		});
	}

	return finalFilters;
};

export const OutlookFilterSchema = z.object({
	value: z.string(),
	label: z.string(),
	id: z.string(),
	color: z.string(),
	name: z.string(),
});

export type OutlookFilter = z.infer<typeof OutlookFilterSchema>;
